import React, { useCallback, useState } from 'react';
import { Button, Spinner } from 'react-bootstrap';
import PollCreateForm from './PollCreateForm';
import PollsList from './PollsList';
import PollDetails from './PollDetails';
import { deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/firebase-config';
import { useUser } from '../../context/UserContext';
import { useMembership } from '../../context/MembershipContext';
import { useTenant } from '../../context/TenantContext';
import {
  canCreateContentForTenant,
  effectiveRole,
} from '../../platform/membershipPermissions';
import { ROLES } from '../../platform/constants';
import ModulePageHeader from '../common/ModulePageHeader';
import useBrowserBackStack from '../../hooks/useBrowserBackStack';
import { useToast } from '../../platform/toast/ToastProvider';
import './Polls.css';

function Polls() {
  const { user, loading } = useUser();
  const { memberships } = useMembership();
  const { tenantId } = useTenant();
  const [selectedPoll, setSelectedPoll] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [pollToEdit, setPollToEdit] = useState(null);

  const role = effectiveRole(memberships, tenantId, user);
  const canCreatePoll = canCreateContentForTenant(memberships, tenantId, user);
  const isAdmin = role === ROLES.ADMIN;
  const isLocalAdmin = role === ROLES.LOCAL_ADMIN;
  const toast = useToast();

  const resetToList = useCallback(() => {
    setShowCreateForm(false);
    setSelectedPoll(null);
    setPollToEdit(null);
  }, []);

  const onList = !showCreateForm && !selectedPoll && !pollToEdit;
  const closeDetailView = useBrowserBackStack(!onList, resetToList);

  const toggleCreateForm = () => {
    if (showCreateForm) {
      closeDetailView();
      return;
    }
    setShowCreateForm(true);
    setSelectedPoll(null);
    setPollToEdit(null);
  };

  const handlePollCreated = () => {
    setShowCreateForm(false);
    setSelectedPoll(null);
    setPollToEdit(null);
  };

  const handleEditPoll = (poll) => {
    setPollToEdit(poll);
    setShowCreateForm(true);
    setSelectedPoll(null);
  };

  const handleDeletePoll = async (pollId) => {
    try {
      await deleteDoc(doc(db, 'polls', pollId));
      resetToList();
    } catch (error) {
      console.error('Error deleting poll:', error);
      toast.error('Brisanje ankete ni uspelo. Poskusite znova.');
      throw error;
    }
  };

  const headerTitle = selectedPoll?.title || pollToEdit?.title
    || (showCreateForm ? 'Ustvari anketo' : 'Ankete');

  if (loading) {
    return (
      <div className="polls-list-loading">
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Nalaganje...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="platform-module-container polls-container">
        <ModulePageHeader
          eyebrow={!onList ? 'Ankete' : undefined}
          title={headerTitle}
          subtitle={
            onList ? 'Udeležite se anket in si oglejte rezultate.' : undefined
          }
          actions={(
            <>
              {!selectedPoll && !pollToEdit && canCreatePoll && (
                <Button variant="primary" onClick={toggleCreateForm}>
                  {showCreateForm ? 'Nazaj na ankete' : 'Ustvari anketo'}
                </Button>
              )}
              {(selectedPoll || pollToEdit) && (
                <Button variant="primary" onClick={closeDetailView}>
                  Nazaj na ankete
                </Button>
              )}
            </>
          )}
        />

        {showCreateForm ? (
          <PollCreateForm onPollCreated={handlePollCreated} pollToEdit={pollToEdit} />
        ) : selectedPoll ? (
          <PollDetails
            poll={selectedPoll}
            isAdmin={isAdmin}
            onEditPoll={handleEditPoll}
            onBackToList={closeDetailView}
          />
        ) : (
          <PollsList
            onSelectPoll={setSelectedPoll}
            onEditPoll={handleEditPoll}
            isAdmin={isAdmin}
            isLocalAdmin={isLocalAdmin}
            onDeletePoll={handleDeletePoll}
          />
        )}
    </div>
  );
}

export default Polls;
